import { useEffect, useMemo, useState } from 'react';
import { addDays, formatDateLabel, formatTimeRange, isWithinNotice, tenantNow, type DaySlots, type NormalizedSlot } from '../domain/dates';
import type { SlotSelection } from '../domain/payload';
import type { BookingPolicy } from '../api/types';
import { Field } from './fields/Field';
import { ChevronLeft, ChevronRight } from './icons';

/**
 * Date + arrival-time picker. Deliberately prop-driven and context-free: the booking form and the
 * customer portal both render it from different state shapes, and reaching into `FormContext`
 * would also drag the Stripe bridge into the portal bundle (see tests/build/bundle.spec.ts).
 *
 * Each caller supplies a thin adapter — `SlotPickerSection` below for the booking form, and
 * `ReschedulePanel` renders it directly for rescheduling.
 */
export interface SlotPickerProps {
  days: DaySlots[];
  /** First date of the loaded window; null before the first load. */
  from: string | null;
  status: 'idle' | 'loading' | 'ready' | 'error';
  loadError: string | null;
  selected: SlotSelection | null;
  /** Notice period that makes near-term slots unbookable. Booking policy, or reschedule policy. */
  policy: BookingPolicy | null;
  timeZone: string;
  timeFormat: '12' | '24';
  now: () => Date;
  label: string;
  required?: boolean;
  errors?: string[];
  /** Allow paging to dates before `from`. A new booking never needs this; a reschedule does. */
  allowEarlier?: boolean;
  onSelect: (slot: SlotSelection | null) => void;
  onLoadRange: (from: string) => void;
}

const WEEK = 7;

function zoneAbbreviation(timeZone: string, date: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' }).formatToParts(new Date(`${date}T12:00:00Z`));
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
}

export function SlotPicker({
  days,
  from,
  status,
  loadError,
  selected,
  policy,
  timeZone,
  timeFormat,
  now,
  label,
  required,
  errors = [],
  allowEarlier = false,
  onSelect,
  onLoadRange,
}: SlotPickerProps) {
  const isBookable = (s: NormalizedSlot) => s.available && !isWithinNotice(s, policy, timeZone, now());
  const dayHasSlots = (d: DaySlots) => !d.holiday && d.slots.some(isBookable);

  const [page, setPage] = useState(0);
  const [selectedDate, setSelectedDate] = useState<string | null>(selected?.date ?? null);

  useEffect(() => {
    setPage(0);
    if (!selectedDate || !days.some((d) => d.date === selectedDate)) {
      setSelectedDate(selected?.date ?? days.find(dayHasSlots)?.date ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, days.length]);

  const pages = Math.max(1, Math.ceil(days.length / WEEK));
  const visible = useMemo(() => days.slice(page * WEEK, page * WEEK + WEEK), [days, page]);
  const selectedDay = days.find((d) => d.date === selectedDate) ?? null;
  const first = visible[0]?.date;
  const last = visible[visible.length - 1]?.date;
  const rangeLabel = first && last ? `${formatDateLabel(first, 'en-US', { month: 'long', day: 'numeric' })} – ${formatDateLabel(last, 'en-US', { month: 'long', day: 'numeric', year: 'numeric' })}` : '';
  const loading = status === 'loading';
  const today = tenantNow(timeZone, now()).date;
  // Back one week from the loaded window, never past today. Stepping back by the whole window
  // (45 days) was what made the arrow look like it skipped a month and a half.
  const canGoEarlier = allowEarlier && !!from && from > today && page === 0;
  const weekBack = from ? addDays(from, -WEEK) : today;
  const earlierFrom = weekBack > today ? weekBack : today;

  return (
    <Field fieldKey="service_date" label={label} required={required} errors={errors} group>
      {() => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="cbf-weeknav">
            {canGoEarlier ? (
              <button type="button" className="cbf-btn cbf-btn-secondary cbf-btn-sm" aria-label="Earlier dates" disabled={loading} onClick={() => onLoadRange(earlierFrom)}>
                <ChevronLeft />
              </button>
            ) : (
              <button type="button" className="cbf-btn cbf-btn-secondary cbf-btn-sm" aria-label="Previous week" disabled={page === 0 || loading} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                <ChevronLeft />
              </button>
            )}
            <strong aria-live="polite">{loading ? 'Loading availability…' : rangeLabel}</strong>
            {page + 1 < pages ? (
              <button type="button" className="cbf-btn cbf-btn-secondary cbf-btn-sm" aria-label="Next week" disabled={loading} onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}>
                <ChevronRight />
              </button>
            ) : (
              <button type="button" className="cbf-btn cbf-btn-secondary cbf-btn-sm" aria-label="Later dates" disabled={loading || !from} onClick={() => from && onLoadRange(addDays(from, days.length || 45))}>
                <ChevronRight />
              </button>
            )}
          </div>

          {status === 'error' ? <p className="cbf-error">{loadError}</p> : null}

          <div className="cbf-days" role="group" aria-label="Choose a day">
            {visible.map((d) => {
              const enabled = dayHasSlots(d);
              return (
                <button
                  key={d.date}
                  type="button"
                  className="cbf-day"
                  aria-pressed={d.date === selectedDate}
                  disabled={!enabled}
                  aria-label={`${formatDateLabel(d.date, 'en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${enabled ? '' : ', no availability'}`}
                  onClick={() => setSelectedDate(d.date)}
                >
                  <span aria-hidden="true">{formatDateLabel(d.date, 'en-US', { weekday: 'short' })}</span>
                  <strong aria-hidden="true">{formatDateLabel(d.date, 'en-US', { day: 'numeric' })}</strong>
                </button>
              );
            })}
          </div>

          {selectedDay ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <span className="cbf-label" id="cbf-slots-label">
                Arrival time — {formatDateLabel(selectedDay.date, 'en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </span>
              <div className="cbf-slots" role="group" aria-labelledby="cbf-slots-label">
                {selectedDay.slots.map((s) => {
                  const on = !!selected && selected.date === s.date && selected.hours === s.hours && selected.minutes === s.minutes;
                  const label = formatTimeRange(s.hours, s.minutes, s.arrivalWindow, timeFormat);
                  // "8:00 to 9:00 AM" reads better than the en dash under a screen reader.
                  const spoken = label.replace(' – ', ' to ');
                  return (
                    <button
                      key={`${s.hours}:${s.minutes}`}
                      type="button"
                      className="cbf-slot"
                      aria-pressed={on}
                      disabled={!isBookable(s)}
                      aria-label={`${spoken}${isBookable(s) ? '' : ', unavailable'}`}
                      onClick={() => onSelect(on ? null : { date: s.date, hours: s.hours, minutes: s.minutes, arrivalWindow: s.arrivalWindow })}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
              {selected ? (
                <p className="cbf-help">
                  Selected: <strong style={{ color: '#2b3540' }}>{formatDateLabel(selected.date, 'en-US', { weekday: 'long', month: 'long', day: 'numeric' })}, {formatTimeRange(selected.hours, selected.minutes, selected.arrivalWindow, timeFormat)}</strong>
                  {selected.arrivalWindow ? ' — we arrive within this window' : ''} · Times shown in {zoneAbbreviation(timeZone, selected.date) || timeZone}
                </p>
              ) : null}
            </div>
          ) : !loading && days.length ? (
            <p className="cbf-help">No availability in this range — try later dates.</p>
          ) : null}
        </div>
      )}
    </Field>
  );
}
